import { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import { Ingredient, FormulationBatchRow, User } from '../types/Types';
import { useToast } from '../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

export const useRNDetail = () => {
  const { showToast } = useToast();
  const { t } = useTranslation();

  // Current ingredient state
  const [ingredient, setIngredient] = useState<Ingredient | null>(null);
  const [loadingIngredient, setLoadingIngredient] = useState<boolean>(false);

  // List of ingredients for search/pagination
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loadingList, setLoadingList] = useState<boolean>(false);
  const [errorList, setErrorList] = useState<string | null>(null);

  // Search and pagination
  const [search, setSearchState] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 300);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [absoluteTotal, setAbsoluteTotal] = useState<number>(0);
  const [reloadFlag, setReloadFlag] = useState(0);
  const requestId = useRef(0);
  const lastFetchKeyRef = useRef<string | null>(null);

  const setSearch = useCallback((value: string) => {
    requestId.current += 1;
    setPage(1);
    setIngredients([]);
    setLoadingList(false);
    setErrorList(null);
    setSearchState(value);
  }, []);

  const apiUrl = process.env.REACT_APP_API_URL ?? '';
  const baseUrl = `${apiUrl}/api/ingredients`;

  // Users list for prepared/approved dropdowns
  const [usersList, setUsersList] = useState<User[]>([]);
  useEffect(() => {
    const token = localStorage.getItem('token') ?? '';
    axios
      .get<User[]>(`${apiUrl}/api/users`, { headers: { 'x-auth-token': token } })
      .then((res) => setUsersList(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [apiUrl]);

  const signIngredient = useCallback(
    async (id: string, field: 'prepared' | 'approved', pin: string): Promise<Ingredient | null> => {
      const token = localStorage.getItem('token') ?? '';
      const res = await axios.post(
        `${baseUrl}/${id}/sign/${field}`,
        { pin },
        { headers: { 'x-auth-token': token } }
      );
      const signed = res.data as Ingredient;
      setIngredient(signed);
      return signed;
    },
    [baseUrl]
  );

  const removeIngredientSignature = useCallback(
    async (id: string, field: 'prepared' | 'approved'): Promise<Ingredient | null> => {
      const token = localStorage.getItem('token') ?? '';
      const res = await axios.delete(`${baseUrl}/${id}/sign/${field}`, {
        headers: { 'x-auth-token': token },
      });
      const updated = res.data as Ingredient;
      setIngredient(updated);
      return updated;
    },
    [baseUrl]
  );

  // Fetch latest ingredient (like DocumentsMaterial pattern)
  const fetchLatestIngredient = useCallback(async () => {
    setLoadingIngredient(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get<{
        ingredients: Ingredient[];
        totalCount: number;
      }>(baseUrl, {
        headers: { 'x-auth-token': token },
        params: {
          sort: 'createdAt',
          order: 'desc',
          page: 1,
          limit: 1,
        },
      });

      if (response.data.ingredients.length > 0) {
        setIngredient(response.data.ingredients[0]);
      } else {
        setIngredient(null);
      }
    } catch (err: any) {
      setIngredient(null);
    } finally {
      setLoadingIngredient(false);
    }
  }, [baseUrl]);

  // Fetch ingredients list for search/dropdown (with accumulation like Partners)
  const fetchIngredientsList = useCallback(async (pageToFetch: number, resetList = false) => {
    const reqId = ++requestId.current;
    setLoadingList(true);
    setErrorList(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get<{
        ingredients: Ingredient[];
        totalCount: number;
        page: number;
        totalPages: number;
      }>(baseUrl, {
        headers: { 'x-auth-token': token },
        params: {
          search: debouncedSearch,
          sort: 'createdAt',
          order: 'desc',
          page: pageToFetch,
          limit: 10,
        },
      });

      if (reqId !== requestId.current) return;

      if (resetList || pageToFetch === 1) {
        setIngredients(response.data.ingredients);
      } else {
        setIngredients(prev => [...prev, ...response.data.ingredients]);
      }
      setTotal(response.data.totalCount);
      setAbsoluteTotal(response.data.totalCount);
      setPage(response.data.page);
      setTotalPages(response.data.totalPages);
    } catch (err: any) {
      if (reqId !== requestId.current) return;
      const message = err.response?.data?.message || err.message || 'Failed to fetch ingredients';
      setErrorList(message);
      if (resetList || pageToFetch === 1) setIngredients([]);
    } finally {
      if (reqId === requestId.current) setLoadingList(false);
    }
  }, [baseUrl, debouncedSearch]);

  // Load a specific ingredient by ID
  const loadIngredientById = useCallback(async (id: string) => {
    setLoadingIngredient(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get<Ingredient>(`${baseUrl}/${id}`, {
        headers: { 'x-auth-token': token },
      });

      setIngredient(response.data);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to load ingredient';
      showToast(message, 'error');
    } finally {
      setLoadingIngredient(false);
    }
  }, [baseUrl, showToast]);

  // Create a new ingredient
  const createIngredient = async (data: {
    items: string;
    batch?: number;
    batchUnit?: string | null;
    formulationBatch?: FormulationBatchRow[];
    waste?: number;
    plannedWaste?: number;
    description?: string;
    validFrom?: string;
    validTo?: string;
    accountingType?: string;
    workOrderDocument?: string;
    status?: string[];
  }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.post(baseUrl, data, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json',
        },
      });

      setIngredient(response.data.ingredient || response.data);
      showToast(t('toast.created', { item: t('sidebar.basicSpecification') }), 'success');
      await fetchIngredientsList(1, true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to create ingredient';
      const messageKey = err.response?.data?.messageKey;
      const translatedMessage = messageKey ? t(messageKey) : message;
      showToast(translatedMessage, 'error');
      throw err;
    }
  };

  // Update an existing ingredient
  const updateIngredient = async (id: string, data: {
    items?: string;
    batch?: number;
    batchUnit?: string | null;
    formulationBatch?: FormulationBatchRow[];
    waste?: number;
    plannedWaste?: number;
    description?: string;
    validFrom?: string;
    validTo?: string;
    accountingType?: string;
    workOrderDocument?: string;
    status?: string[];
  }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.put(`${baseUrl}/${id}`, data, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json',
        },
      });

      setIngredient(response.data.ingredient || response.data);
      showToast(t('toast.updated', { item: t('sidebar.basicSpecification') }), 'success');
      await fetchIngredientsList(1, true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to update ingredient';
      const messageKey = err.response?.data?.messageKey;
      const translatedMessage = messageKey ? t(messageKey) : message;
      showToast(translatedMessage, 'error');
      throw err;
    }
  };

  // Archive an ingredient
  const archiveIngredient = async (id: string, reason: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.post(`${baseUrl}/${id}/archive`, { archivedReason: reason }, {
        headers: { 'x-auth-token': token, 'Content-Type': 'application/json' },
      });

      setIngredient(null);
      showToast(t('ingredients.archive.success', 'Ingredient archived'), 'success');
      await fetchIngredientsList(1, true);
      await fetchLatestIngredient();
      return response.data.ingredient;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to archive ingredient';
      showToast(message, 'error');
      throw err;
    }
  };

  // Delete an ingredient
  const deleteIngredient = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      await axios.delete(`${baseUrl}/${id}`, {
        headers: {
          'x-auth-token': token,
        },
      });

      showToast(t('toast.deleted', { item: t('sidebar.basicSpecification') }), 'success');

      // Refresh list and auto-load latest ingredient (like DocumentsMaterial pattern)
      await fetchIngredientsList(1, true);
      await fetchLatestIngredient();
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to delete ingredient';
      const messageKey = err.response?.data?.messageKey;
      const translatedMessage = messageKey ? t(messageKey) : message;
      showToast(translatedMessage, 'error');
      throw err;
    }
  };

  // Clear current ingredient
  const clearIngredient = () => {
    setIngredient(null);
  };

  // NOTE: no auto-load of the latest OS ingredient on mount. The РН screen defaults
  // to the latest РН work order instead (RNWorkOrder's rnList-based auto-load) — the
  // OS ingredient is loaded only when an ident/work order is selected.

  // Reset to page 1 when search changes (must run before the fetch effect)
  useLayoutEffect(() => {
    setPage(1);
    setIngredients([]);
  }, [debouncedSearch]);

  // Fetch list when search, page, or reload flag changes
  useEffect(() => {
    const fetchKey = `${debouncedSearch}\u0000${page}\u0000${reloadFlag}`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    if (page === 1) fetchIngredientsList(1, true);
    else fetchIngredientsList(page, false);
  }, [fetchIngredientsList, page, reloadFlag, debouncedSearch]);

  // Reload function for modal open — resets state and forces fresh fetch
  const reloadIngredientsList = useCallback(() => {
    setPage(1);
    setIngredients([]);
    setReloadFlag(prev => prev + 1);
  }, []);

  return {
    ingredient,
    clearIngredient,
    loadingIngredient,
    ingredients,
    loadingList,
    errorList,
    page,
    totalPages,
    total,
    absoluteTotal,
    setPage,
    search,
    setSearch,
    createIngredient,
    loadIngredientById,
    updateIngredient,
    deleteIngredient,
    archiveIngredient,
    usersList,
    reloadIngredientsList,
    signIngredient,
    removeIngredientSignature,
  };
};
